/**
 * @description Menu da conta do usuário no header (mobile: top sheet; desktop: painel).
 * @module AvatarMenu
 */

import { Avatar, IconButton } from "@mui/material";
import { useCallback } from "react";

import { TermOfUseDialog } from "@/pages/TermsOfUse/TermOfUseDialog";
import { useSessionContext } from "@/contexts/SessionContext";

import { useUserMenuActions } from "./hooks/useUserMenuActions";
import { useHeaderCollapse } from "./HeaderCollapseContext";
import { MobileTopSheet } from "@/components/common/MobileTopSheet";
import {
  UserMenuPanelContent,
} from "./UserMenuPanelContent";
import { UserMenuDesktopPanel } from "./UserMenuDesktopPanel";

interface AvatarMenuProps {
  isMobile?: boolean;
  handleMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  handleMenuClose: () => void;
  anchorEl: null | HTMLElement;
}

export function AvatarMenu({
  isMobile = false,
  handleMenuOpen,
  handleMenuClose,
  anchorEl,
}: AvatarMenuProps) {
  const { isOpenTermDialog } = useSessionContext();
  const {
    mobileHeaderOffset,
    mobileOpenPanel,
    toggleMobileHeaderPanel,
    closeMobileHeaderPanel,
  } = useHeaderCollapse();
  const isOpen = isMobile
    ? mobileOpenPanel === "userMenu"
    : Boolean(anchorEl);

  const closeMenu = useCallback(() => {
    if (isMobile) {
      closeMobileHeaderPanel();
      return;
    }
    handleMenuClose();
  }, [closeMobileHeaderPanel, handleMenuClose, isMobile]);

  const {
    user,
    menuItems,
    navigateTo,
    openSupport,
    openTerms,
    logout,
    accessLabel,
    initials,
  } = useUserMenuActions(closeMenu);

  const subtitle = user?.username
    ? `${accessLabel} · ${user.username}`
    : accessLabel;

  return (
    <>
      <IconButton
        onClick={(event) => {
          event.stopPropagation();
          if (isMobile) {
            toggleMobileHeaderPanel("userMenu");
            return;
          }
          if (isOpen) {
            handleMenuClose();
          } else {
            handleMenuOpen(event);
          }
        }}
        aria-label={isOpen ? "Fechar menu da conta" : "Abrir menu da conta"}
        aria-expanded={isOpen}
        sx={{ p: 0 }}
      >
        <Avatar
          sx={{
            width: 36,
            height: 36,
            bgcolor: "neutral0.main",
            color: "primary800.main",
            fontWeight: 700,
            fontSize: "0.9375rem",
            border: "2px solid",
            borderColor: "rgba(255,255,255,0.35)",
          }}
        >
          {initials}
        </Avatar>
      </IconButton>

      {isMobile ? (
        <MobileTopSheet
          open={isOpen}
          onClose={closeMenu}
          title={user?.name || "Minha conta"}
          subtitle={subtitle}
          headerLayer
          size="large"
          topOffset={mobileHeaderOffset}
        >
          <UserMenuPanelContent
            userName={user?.name}
            userUsername={user?.username}
            accessLabel={accessLabel}
            initials={initials}
            menuItems={menuItems}
            showProfileHero={false}
            onNavigate={navigateTo}
            onOpenSupport={openSupport}
            onOpenTerms={openTerms}
            onLogout={logout}
          />
        </MobileTopSheet>
      ) : (
        <UserMenuDesktopPanel
          open={isOpen}
          anchorEl={anchorEl}
          onClose={handleMenuClose}
          userName={user?.name}
          userUsername={user?.username}
          accessLabel={accessLabel}
          initials={initials}
          menuItems={menuItems}
          onNavigate={navigateTo}
          onOpenSupport={openSupport}
          onOpenTerms={openTerms}
          onLogout={logout}
        />
      )}

      {isOpenTermDialog ? <TermOfUseDialog isOpen={isOpenTermDialog} /> : null}
    </>
  );
}
